<template>
  <div
    v-if="status"
    class="status_container"
    :class="{ dragged: isDragedOver }"
    v-on="$listeners"
    @dragover.prevent="isDragedOver = true"
    @dragenter.prevent
    @dragleave="isDragedOver = false"
    @drop="insertQuestion($event)"
  >
    <BaseExpensionPanels>
      <BaseExpensionPanel
        v-model="isExpanded"
        class="disagreed-panel"
        expand
        eager
        background-color="secondary"
        @click="onExpand"
      >
        <template slot="header">
          <!-- HEADER -->
          <div class="status_header">
            <div class="header_left">
              <div class="number-status" v-text="indexStatus" />
              <div class="status_name" v-text="status.name" />
              <div v-if="needActionArrow" class="actions d-flex align-center">
                <base-button
                  width="29"
                  class="btn-action mr-2"
                  color="primary"
                  :disabled="upArrowDisabled"
                  @click.stop="moveStatus('move_up')"
                >
                  <v-icon>
                    {{ "$mdiArrowUp" }}
                  </v-icon>
                </base-button>
                <base-button
                  width="29"
                  class="btn-action"
                  color="primary"
                  :disabled="downArrowDisabled"
                  @click.stop="moveStatus('move_down')"
                >
                  <v-icon>
                    {{ "$mdiArrowDown" }}
                  </v-icon>
                </base-button>
              </div>
            </div>
            <div class="header_right">
              <!-- edit workflow status button -->
              <BaseButton
                class="mr-2"
                icon="$mdiPencilOutline"
                @click.stop="$emit('editStatus', status)"
              >
                {{ $t("utils.edit") }}
              </BaseButton>
              <BaseButton
                v-if="workflow.primaryStatus != status.id"
                icon="$mdiTrashCanOutline"
                color="secondary"
                @click.stop="$emit('deleteStatus', status)"
              >
                {{ $t("utils.delete") }}
              </BaseButton>
            </div>
          </div>
        </template>
        <template slot="content">
          <!-- BODY -->
          <div class="status_body">
            <v-tabs v-model="tab" color="primary" slider-color="primary">
              <v-tab class="d-flex align-center">
                <BaseIcon
                  icon="$mdiCheck"
                  small
                  class="mr-2 text-description-normal"
                />
                <BaseDescription
                  class="text-description"
                  v-text="$t('utils.customFields')"
                />
              </v-tab>
              <v-tab>
                <BaseIcon
                  icon="$mdiCrosshairsGps"
                  small
                  class="mr-2 text-description-normal"
                />
                <BaseDescription
                  class="text-description"
                  v-text="$t('utils.targetStatus')"
                />
              </v-tab>
              <v-tab>
                <BaseIcon
                  icon="$mdiCogs"
                  small
                  class="mr-2 text-description-normal"
                />
                <BaseDescription
                  class="text-description"
                  v-text="$t('utils.choicesOfAction')"
                />
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <!-- CHAMP personnalisés -->
              <v-tab-item class="py-4 px-1">
                <div class="status_fields">
                  <WorkflowStatusCategory
                    v-for="(statusCategory, i) in groupedQuestionsByCategory"
                    :key="i"
                    :status-category="statusCategory"
                    :is-first="statusCategory.isFirst"
                    :is-last="statusCategory.isLast"
                    @statusCategoryMoved="fetchStatusCategories"
                    @editCategory="editCategory"
                    @removeCategory="removeCategory"
                    @addQuestion="addQuestion"
                    @editingQuestion="editQuestion"
                    @removeQuestion="removeQuestion"
                  />
                </div>
              </v-tab-item>
              <!-- Statut cible -->
              <v-tab-item class="py-4 px-1">
                <WorkflowDetailStatusTargetStatuses
                  :status="status"
                  :workflow="workflow"
                />
              </v-tab-item>
              <!-- Choix des actions -->
              <v-tab-item class="py-4 px-1">
                <WorkflowStatusActions :status="status" />
              </v-tab-item>
            </v-tabs-items>
          </div>
        </template>
      </BaseExpensionPanel>
    </BaseExpensionPanels>
    <!-- DIALOG SECITION -->
    <WorkflowStatusQuestionCreateAndEditDialog
      :dialog-state="openCreateAndEditQuestionDialog"
      :workflow-id="workflow.id"
      :status="status"
      :status-category="activeQuestionCategory"
      :active-question="activeQuestion"
      @questionCreated="handleQuestionCreated()"
      @questionUpdated="handleQuestionCreated()"
      @close="closeDialogs()"
    />
    <WorkflowStatusQuestionDeleteDialog
      :dialog-state="openDeleteQuestionDialog"
      :workflow-id="workflow.id"
      :status="status"
      :active-question="activeQuestion"
      @questionDeleted="handleQuestionDeleted"
      @close="closeDialogs()"
    />
    <WorkflowEditStatusCategory
      :dialog-state="openEditCategoryDialog"
      :active-category="activeQuestionCategory"
      @categoryUpdated="fetchStatusCategories()"
      @close="closeDialogs()"
    />
    <WorkflowRemoveStatusCategory
      :dialog-state="openRemoveCategoryDialog"
      :active-category="activeQuestionCategory"
      @categoryRemoved="fetchStatusQuestions()"
      @close="closeDialogs()"
    />
  </div>
</template>

<script>
import WorkflowStatusQuestionCreateAndEditDialog from "./WorkflowStatusQuestionCreateAndEditDialog";
import WorkflowStatusQuestionDeleteDialog from "../Components/WorkflowStatusQuestionDeleteDialog";
import WorkflowDetailStatusTargetStatuses from "./WorkflowDetailStatusTargetStatuses";
import WorkflowStatusActions from "./WorkflowStatusActions";
import WorkflowStatusCategory from "./WorkflowStatusCategory.vue";
import WorkflowEditStatusCategory from "./WorkflowEditStatusCategory.vue";
import WorkflowRemoveStatusCategory from "./WorkflowRemoveStatusCategory.vue";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";
import { QuestionCategory } from "@/core/Models/QuestionCategory";
import { getQuestionTemplateOption } from "@/core/Services/questionTemplate.service";
import Question from "@/core/Models/Question";
import { fileURLToFile } from "@/core/Utils/file.utils";

export default {
  name: "WorkflowDetailStatus",
  components: {
    WorkflowStatusQuestionCreateAndEditDialog,
    WorkflowDetailStatusTargetStatuses,
    WorkflowStatusActions,
    WorkflowStatusCategory,
    WorkflowStatusQuestionDeleteDialog,
    WorkflowEditStatusCategory,
    WorkflowRemoveStatusCategory,
  },
  props: {
    workflow: {
      type: Object,
      required: true,
    },
    status: {
      type: Object,
      default: null,
    },
    isLastStatus: {
      type: Boolean,
    },
    indexStatus: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      openCreateAndEditQuestionDialog: false,
      openDeleteQuestionDialog: false,
      openEditCategoryDialog: false,
      openRemoveCategoryDialog: false,
      isLoadingQuestions: false,
      tab: 0,
      isExpanded: false,
      questionCategories: [],
      questions: [],
      activeQuestion: null,
      activeQuestionCategory: null,
      isDragedOver: false,
    };
  },
  computed: {
    upArrowDisabled() {
      return this.status.order <= 1;
    },
    downArrowDisabled() {
      return this.status.order === this.workflow?.statuses?.length;
    },
    needActionArrow() {
      return this.workflow?.statuses?.length > 1;
    },
    groupedQuestionsByCategory() {
      if (!this.status) return [];
      let groupedQuestions = this.questionCategories
        .map((sc) => ({
          ...sc,
          statusId: this.status.id,
          isFirst:
            sc.order ===
            Math.min(...this.questionCategories.map((tmpSC) => tmpSC.order)),
          isLast:
            sc.order ===
            Math.max(...this.questionCategories.map((tmpSC) => tmpSC.order)),
          questions: this.questions
            .filter((q) => q.category?.id === sc.id)
            .sort((a, b) => a.order - b.order),
        }))
        .filter((sc) => sc.questions.length)
        .sort((a, b) => a.order - b.order);
      const withoutCategoryGroup = {
        id: null,
        name: this.$t("category.uncategorized"),
        statusId: this.status.id,
        questions: this.questions
          .filter((q) => !q.category)
          .sort((a, b) => a.order - b.order),
      };
      if (withoutCategoryGroup.questions.length) {
        groupedQuestions.unshift(withoutCategoryGroup);
      }
      return groupedQuestions;
    },
  },
  watch: {
    status(newVal, oldVal) {
      if (newVal?.name != oldVal?.name && !newVal.questions?.length)
        this.initialize();
    },
  },
  methods: {
    onExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.initialize();
      }
    },
    async insertQuestion(event) {
      this.isDragedOver = false;
      const droppedDataString = event.dataTransfer.getData("application/json");
      const droppedObject = JSON.parse(droppedDataString);
      try {
        if (droppedObject.helpFile) {
          droppedObject.helpFile = await fileURLToFile(
            droppedObject.helpFile,
            droppedObject.helpFile?.split("/").reverse()[0]
          );
        }
        const question = await this.$store.dispatch(
          `workflow/${
            droppedObject.questionType !== "DATATYPE"
              ? "createWorkflowStatusQuestion"
              : "createWorkflowStatusDatatypeQuestion"
          }`,
          {
            organizationId: this.$route.params.organizationId,
            workflowId: this.workflow.id,
            statusId: this.status.id,
            payload: {
              ...droppedObject,
              choices: droppedObject.choices?.length
                ? droppedObject.choices
                : await this.fetchQuestionTemplateOption(droppedObject),
            },
          }
        );
        if (question.datatype && !isNaN(question.datatype)) {
          question.typeQuestion = this.datatypes.items.find(
            (ct) => ct.id === question.datatype
          ).typeQuestion;
        }
        this.handleQuestionCreated();
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.fieldCreatedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async fetchQuestionTemplateOption(questonTemplate) {
      const requireOptionsRegex =
        /^SELECT|RADIOBUTTON|CHECKBOX|DATAGRID|ROW_DATAGRID$/;
      if (!requireOptionsRegex.test(questonTemplate.typeQuestion)) return [];
      try {
        const options = await getQuestionTemplateOption({
          organizationId: this.$route.params.organizationId,
          questionTemplateId: questonTemplate.id,
        });
        return options;
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async initialize() {
      this.isLoadingQuestions = true;
      await this.fetchStatusQuestions();
      this.isLoadingQuestions = false;
      this.$store.dispatch("workflow/fetchOrganizationCollaborators", {
        organizationId: this.$route.params.organizationId,
      });
    },
    async fetchStatusQuestions() {
      await this.fetchStatusCategories();
      // GET Status Question
      let page = 1;
      let tmp = [];
      while (page) {
        let res = await WorkflowAPI.fetchorganizationWorkflowStatusQuestions(
          this.$route.params.organizationId,
          this.workflow.id,
          this.status.id,
          page
        );
        page = res.data.next;
        tmp = [...tmp, ...res.data.results];
      }
      this.questions = tmp.map((q) => new Question(q));
    },
    async fetchStatusCategories() {
      let page = 1;
      let tmp = [];
      while (page) {
        let res = await WorkflowAPI.getWorkflowStatusCategories({
          organizationId: this.$route.params.organizationId,
          workflowId: this.workflow.id,
          statusId: this.status.id,
          page: page,
        });
        page = res.data.next;
        tmp = [...tmp, ...res.data.results];
      }
      this.questionCategories = tmp.map((s) => new QuestionCategory(s));
    },
    async moveStatus(action) {
      this.$emit("moveStatus", { status: this.status, action });
    },
    getDisplayTextForTypeQuestion(type) {
      switch (type) {
        case "TEXT":
          return this.$t("question.types.text");
        case "TEXTAREA":
          return this.$t("question.types.textarea");
        case "NUMBER":
          return this.$t("question.types.number");
        case "DATE":
          return this.$t("question.types.date");
        case "DATETIME":
          return this.$t("question.types.datetime");
        case "RADIOBUTTON":
          return this.$t("question.types.radio");
        case "CHECKBOX":
          return this.$t("question.types.checkbox");
        case "SELECT":
          return this.$t("question.types.select");
        case "FILE":
          return this.$t("question.types.file");
        case "SIGNATURE":
          return this.$t("question.types.signature");
        case "DATAGRID":
          return this.$t("question.types.datagrid");
        case "ROW_DATAGRID":
          return this.$t("question.types.rowDatagrid");
        case "MESSAGE":
          return this.$t("question.types.message");
      }
    },
    setPrimaryStatus(statusId) {
      const payload = {
        primary_status: statusId,
      };
      if (this.workflow.primaryStatus === statusId) {
        payload.primary_status = null;
      }
      this.$store
        .dispatch("workflow/updateWorkflow", {
          organizationId: this.$route.params.organizationId,
          workflowId: this.workflow.id,
          payload,
        })
        .then(() => {
          if (payload.primary_status !== null) {
            this.$store.dispatch("snackbar/active", {
              message: this.$t(
                "workflow.status.snackbar.success.statusStartDefinedSuccessfully"
              ),
              type: "SUCCESS",
            });
          } else {
            this.$store.dispatch("snackbar/active", {
              message: this.$t(
                "workflow.status.snackbar.success.statusStartDeletedSuccessfully"
              ),
              type: "SUCCESS",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("snackbar/active", {
            message: this.$t("workflow.status.snackbar.success.errorDeleting"),
            type: "ERROR",
          });
        });
    },
    addQuestion({ statusCategory }) {
      this.activeQuestionCategory = statusCategory;
      this.openCreateAndEditQuestionDialog = true;
    },
    handleQuestionCreated() {
      this.closeDialogs();
      this.fetchStatusQuestions();
    },
    closeDialogs() {
      this.openCreateAndEditQuestionDialog = false;
      this.openDeleteQuestionDialog = false;
      this.openEditCategoryDialog = false;
      this.openRemoveCategoryDialog = false;
      this.activeQuestion = null;
      this.activeQuestionCategory = null;
    },
    editQuestion({ question, statusCategory }) {
      this.activeQuestion = question;
      this.activeQuestionCategory = statusCategory;
      this.openCreateAndEditQuestionDialog = true;
    },
    removeQuestion({ question }) {
      this.activeQuestion = question;
      this.openDeleteQuestionDialog = true;
    },
    handleQuestionDeleted(questionId) {
      this.questions = this.questions.filter((q) => q.id !== questionId);
    },
    setToggleStatusActionDialog({ event, action, status }) {
      this.$store.commit("workflow/setActiveStatus", status);
      this.$store.commit("workflow/setActiveAction", action);
      this.$store.commit("workflow/setActiveActionExists", !event);
      this.$store.commit("workflow/setToggleStatusActionDialog", true);
    },
    editCategory(params) {
      this.activeQuestionCategory = params;
      this.openEditCategoryDialog = true;
    },
    removeCategory(params) {
      this.activeQuestionCategory = params;
      this.openRemoveCategoryDialog = true;
    },
    handledCategoryRemoved(categoryId) {
      this.closeDialogs();
      this.questionCategories = this.questionCategories.filter(
        (c) => c.id !== categoryId
      );
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .actions .v-btn {
  padding: 0px !important;
}
::v-deep .v-expansion-panel-header {
  padding: 24px;
}
.status_container {
  margin-bottom: 24px;
  box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
}
.dragged {
  .disagreed-panel {
    background: #f4f7ff !important;
  }
}
.status_header {
  display: flex;
  align-items: center;
  .header_left {
    display: flex;
    align-items: center;
    .status_name {
      font-family: "Inter";
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #353542;
      margin-right: 24px;
    }
  }
  .header_right {
    margin-left: auto;
  }
}
.status_body {
  .status_fields {
    .status_fields_actions {
      margin: 16px 0;
      display: flex;
      justify-content: center;
    }
  }
}
.number-status {
  padding: 10px;
  gap: 10px;

  width: 30px;
  height: 30px;

  /* Principale/Foncé */

  background: #2536cc;
  border-radius: 31px;
  margin-right: 12px;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}
.btn-action {
  padding: 7px;
  gap: 10px;
  height: 30px !important;
}
::v-deep .v-btn {
  min-width: 29px !important;
}
</style>
